import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchJSON } from 'api/fetch';
import { Map } from 'immutable';

import { showModal } from 'actions/modals';

import { getCurrentLocationUuid } from 'selectors/session';

import { fetchCreditCards } from 'features/biller/implementations/stripe/thunks';
import { selectLocationBiller } from 'features/biller/selectors';
import { fetchBillerLocations } from 'features/biller/supportActions';
import {
  BillingFrequencyType,
  ExperimentalBundlesApiResponse,
} from 'features/managePlan/types';
import { BILLER_CHANGE_BUNDLE_MODAL } from 'features/modals/constants';
import { getMarketingDiscountCoupon } from 'features/productGrowth/api';

import { trackUxEvent } from 'util/tracking';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';

import { BUNDLE_NAMES, DEFAULT_WORKFLOW_SOURCE } from './constants';
import {
  ManagePlanPageAttributesApiType,
  ProductsApiType,
  PurchasableProductsAPIType,
  SubscriptionsApiType,
} from './slice';

type SubscriptionsResponse = Promise<SubscriptionsApiType>;
type ProductsResponse = Promise<ProductsApiType>;
type PurchasableProductsResponse = Promise<PurchasableProductsAPIType>;
type ManagePlanPageAttributesResponse =
  Promise<ManagePlanPageAttributesApiType>;

export const fetchPurchasableProducts = createAsyncThunk(
  'products/fetchPurchasableProducts',
  (_, { getState }): PurchasableProductsResponse =>
    fetchJSON(`/products/${getCurrentLocationUuid(getState())}`)
);

export const fetchProducts = createAsyncThunk('products/fetchProducts', () =>
  fetchJSON('/tier_products.json')
);

export const fetchFeatures = createAsyncThunk('products/fetchFeatures', () =>
  fetchJSON('/get_features.json')
);

export const fetchMarketingDiscountCoupon = createAsyncThunk(
  'products/fetchMarketingDiscountCoupon',
  getMarketingDiscountCoupon
);

// Tip Manager Actions
export const fetchTipManagerSubscriptions = createAsyncThunk(
  'tipManager/fetchSubscriptions',
  (): SubscriptionsResponse =>
    fetchJSON(
      '/biller/product_subscriptions/index?external_identifier=tip_manager'
    )
);

export const fetchProductTipManager = createAsyncThunk(
  'products/fetchTipManagerProduct',
  (_, { getState }): ProductsResponse =>
    fetchJSON(
      `/products/${getCurrentLocationUuid(
        getState()
      )}?external_identifier=tip_manager`
    )
);

export const fetchHiringAssistantProduct = createAsyncThunk(
  'products/fetchHiringAssistantProduct',
  (_, { getState }): ProductsResponse =>
    fetchJSON(
      `/products/${getCurrentLocationUuid(
        getState()
      )}?external_identifier=hiring_assistant`
    )
);

export const fetchTrainingAssistantProduct = createAsyncThunk(
  'products/fetchTrainingAssistantProduct',
  (_, { getState }): ProductsResponse =>
    fetchJSON(
      `/products/${getCurrentLocationUuid(
        getState()
      )}?external_identifier=training_assistant`
    )
);

export const fetchHiringAssistantSubscriptions = createAsyncThunk(
  'hiringAssistant/fetchSubscriptions',
  (): SubscriptionsResponse =>
    fetchJSON(
      '/biller/product_subscriptions/index?external_identifier=hiring_assistant'
    )
);

export const fetchTrainingAssistantSubscriptions = createAsyncThunk(
  'trainingAssistant/fetchSubscriptions',
  (): SubscriptionsResponse =>
    fetchJSON(
      '/biller/product_subscriptions/index?external_identifier=training_assistant'
    )
);

// Task Manager Actions
export const fetchTaskManagerSubscriptions = createAsyncThunk(
  'taskManager/fetchSubscriptions',
  (): SubscriptionsResponse =>
    fetchJSON(
      '/biller/product_subscriptions/index?external_identifier=task_manager'
    )
);

export const fetchProductTaskManager = createAsyncThunk(
  'products/fetchTaskManagerProduct',
  (_, { getState }): ProductsResponse =>
    fetchJSON(
      `/products/${getCurrentLocationUuid(
        getState()
      )}?external_identifier=task_manager`
    )
);

export const fetchManagePlanPageAttributes = createAsyncThunk(
  'products/fetchManagePlanPageAttributes',
  (): ManagePlanPageAttributesResponse =>
    fetchJSON('/monetization/manage_plans/get_attributes')
);

export const fetchExperimentalBundles = createAsyncThunk(
  'products/fetchExperimentalBundles',
  (_, { getState }): Promise<ExperimentalBundlesApiResponse> =>
    fetchJSON(`/experimental_bundles/${getCurrentLocationUuid(getState())}`)
);

export interface ChangeBundlePayload {
  bundleName: string;
  billingCycle: BillingFrequencyType;
  biller: string;
  newLocations?: Map<string, unknown>;
  payrollLocationAdded?: boolean;
}

export const showChangeBundleModal = ({
  bundleName,
  billingCycle,
  biller,
  newLocations,
  payrollLocationAdded = false,
}: ChangeBundlePayload) =>
  showModal(BILLER_CHANGE_BUNDLE_MODAL, {
    deprecatedModal: true,
    fullScreen: true,
    hideCloseIcon: true,
    bundleName,
    billingCycle,
    biller,
    newLocations,
    payrollLocationAdded,
    onDismiss: () => {
      trackUxEvent({
        productArea: PRODUCT_AREAS.BILLING_PAGE,
        eventCategory: EVENT_CATEGORIES.BILLING_PAGE,
        eventAction: EVENT_ACTIONS.DISMISS_CLICKED,
        actionType: TRACK_ACTION_TYPES.CLICK,
        properties: { escape_key: true },
      });
    },
  });

export const changeBundle = createAsyncThunk(
  'managePlan/changeBundle',
  async (
    {
      bundleName,
      billingCycle,
      biller,
      newLocations,
      payrollLocationAdded = false,
    }: ChangeBundlePayload,
    { dispatch }
  ) => {
    await Promise.all([
      dispatch(fetchBillerLocations()),
      dispatch(fetchCreditCards()),
    ]);

    dispatch(
      showChangeBundleModal({
        bundleName,
        billingCycle,
        biller,
        newLocations,
        payrollLocationAdded,
      })
    );
  }
);

// This should only be used in places where you could invoke a change to any bundle,
// but if you know which specific bundle you want,
// you should opt for the specific instead e.g changeToCoreBundle
export interface ChangeToNewBundleData {
  bundleName: string;
  billingFrequency: BillingFrequencyType;
  workflowSource?: string;
}

export function changeToNewBundle(data: ChangeToNewBundleData) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function changeToNewBundleThunk(dispatch: any, getState: any) {
    const items = {
      billingFrequency: data.billingFrequency,
      workflowSource: data.workflowSource || DEFAULT_WORKFLOW_SOURCE,
    };

    const biller = selectLocationBiller(getState());

    // Validate bundle name matches one of the known bundles
    const validBundleNames = [
      BUNDLE_NAMES.CORE,
      BUNDLE_NAMES.GROW,
      BUNDLE_NAMES.SCALE,
    ];
    if (
      !validBundleNames.includes(data.bundleName as typeof BUNDLE_NAMES.CORE)
    ) {
      // eslint-disable-next-line no-console
      console.error(
        `Invalid bundle name: ${
          data.bundleName
        }. Expected one of: ${validBundleNames.join(', ')}`
      );
      return;
    }

    // Dispatch changeBundle which will call showChangeBundleModal
    dispatch(
      changeBundle({
        bundleName: data.bundleName,
        billingCycle: items.billingFrequency,
        biller,
      })
    );
  };
}
